import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  fetchBeatCustomerOutstandingReport,
  fetchBeatCustomerSalesCompReport,
  fetchKpiDivisionWiseSalesReport,
  fetchTargetSaleReport
} from 'Service/ApiService';

const BeatsCustomerReportController = () => {
  const location = useLocation();
  const customer = location.state?.customer;
  const user = JSON.parse(localStorage.getItem('user'));
  const partyCode = customer?.party_code || customer?.partyCode || '';

  const formatIndianShort = (num) => {
    const n = Number(num || 0);
    const sign = n < 0 ? '-' : '';
    const abs = Math.abs(n);

    return `${sign}${(abs / 100000).toFixed(2)} L`;
  };

  // Financial year info
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  const startYear = month >= 4 ? year : year - 1; // FY starts in April
  const currentFY = `${String(startYear).slice(2)}-${String(startYear + 1).slice(2)}`;
  const previousFY = `${String(startYear - 1).slice(2)}-${String(startYear).slice(2)}`;

  // States
  const [cloading, setCloading] = useState(false);
  const [outstanding, setOutstanding] = useState([]);
  const [sales, setSales] = useState([]);
  const [categoryComp, setCategoryComp] = useState([]);
  const [targetsales, setTargetSales] = useState([]);
  // Fetch all reports on mount
  useEffect(() => {
    fetchAllReports();
  }, []);

  const fetchAllReports = async () => {
    setCloading(true);
    try {
      await Promise.all([
        getOutstandingReport(),
        getSalesCompReport(),
        getCategoryCompReport(),
        getTargetSale()
      ]);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setCloading(false);
    }
  };

  // Outstanding Report
  const getOutstandingReport = async () => {
    try {
      const response = await fetchBeatCustomerOutstandingReport(user.userCode, partyCode);
      if (response.success) {
        setOutstanding(
          response.data.partyOutstanding.map((row) => ({
            ...row,
            billAmount: row.billAmount,
            billAmountDisplay: formatIndianShort(row.billAmount)
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching Outstanding Report:', error);
    }
  };

  // Sales Comparison Report
  const getSalesCompReport = async () => {
    try {
      const response = await fetchBeatCustomerSalesCompReport(user.userCode, partyCode);
      if (response.success) {
        const monthOrder = [
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
          'January',
          'February',
          'March'
        ];

        // Backward compatibility: support both old keys (ctd/ytd) and new keys (current_year/previous_year)
        const currentYearData = response.data?.current_year || response.data?.ctd || {};
        const previousYearData = response.data?.previous_year || response.data?.ytd || {};

        const cleanNumber = (value) => Number(String(value ?? '0').replace(/,/g, '')) || 0;

        const merged = monthOrder.map((month) => {
          const cur = currentYearData[month] || {};
          const prev = previousYearData[month] || {};

          const qty1 = cleanNumber(cur.meters);
          const amt1 = cleanNumber(cur.amount);
          const qty2 = cleanNumber(prev.meters);
          const amt2 = cleanNumber(prev.amount);
          const higherYear = amt2 > amt1 ? 'previous' : amt1 > amt2 ? 'current' : 'tie';

          return {
            month,
            qty1,
            amt1,
            qty2,
            amt2,
            higherYear,
            qty1Display: qty1.toFixed(2),
            amt1Display: formatIndianShort(amt1),
            qty2Display: qty2.toFixed(2),
            amt2Display: formatIndianShort(amt2)
          };
        });

        setSales(merged);
      }
    } catch (error) {
      console.error('Error fetching Sales Comparison Report:', error);
    }
  };

  // KPI Division Wise Sales Report
  const getCategoryCompReport = async () => {
    try {
      const response = await fetchKpiDivisionWiseSalesReport(user.userCode, partyCode);
      if (response.success) {
        const cleanNumber = (value) => Number(String(value ?? '0').replace(/,/g, '')) || 0;
        setCategoryComp(buildDivisionWiseSalesRows(response.data, cleanNumber));
      }
    } catch (error) {
      console.error('Error fetching Division Wise Sales Report:', error);
    }
  };

  const buildDivisionWiseSalesRows = (data, cleanNumber) => {
    if (data?.current_year || data?.previous_year) {
      return buildYearRows(data.current_year || [], data.previous_year || [], cleanNumber);
    }

    const ctdArray = Array.isArray(data?.ctd) ? data.ctd : [];
    const ytdArray = Array.isArray(data?.ytd) ? data.ytd : [];
    const ytdByCategory = new Map(ytdArray.map((row) => [getSalesCategory(row), row]));

    return ctdArray.map((row, index) => {
      const category = getSalesCategory(row);
      const ytdRow = ytdByCategory.get(category) || ytdArray[index] || {};
      const qty1 = cleanNumber(row.meters);
      const amt1 = cleanNumber(row.amount);
      const qty2 = cleanNumber(ytdRow.meters);
      const amt2 = cleanNumber(ytdRow.amount);

      return {
        category,
        qty1,
        amt1,
        qty2,
        amt2,
        higherYear: amt2 > amt1 ? 'previous' : amt1 > amt2 ? 'current' : 'tie',
        qty1Display: qty1.toFixed(2),
        amt1Display: formatIndianShort(amt1),
        qty2Display: qty2.toFixed(2),
        amt2Display: formatIndianShort(amt2)
      };
    });
  };

  const getSalesCategory = (row = {}) => row.category || row.division || row.name || 'Others';

  const normalizeYearSales = (yearData) => {
    if (Array.isArray(yearData)) {
      return new Map(yearData.map((row) => [getSalesCategory(row), row]));
    }

    return new Map(
      Object.entries(yearData || {}).map(([label, row]) => [
        getSalesCategory(row) === 'Others' ? label : getSalesCategory(row),
        row || {}
      ])
    );
  };

  const buildYearRows = (currentYearData, previousYearData, cleanNumber) => {
    const currentByCategory = normalizeYearSales(currentYearData);
    const previousByCategory = normalizeYearSales(previousYearData);
    const labels = Array.from(new Set([...previousByCategory.keys(), ...currentByCategory.keys()]));

    return labels.map((label) => {
      const cur = currentByCategory.get(label) || {};
      const prev = previousByCategory.get(label) || {};
      const qty1 = cleanNumber(cur.meters);
      const amt1 = cleanNumber(cur.amount);
      const qty2 = cleanNumber(prev.meters);
      const amt2 = cleanNumber(prev.amount);

      return {
        category: label,
        qty1,
        amt1,
        qty2,
        amt2,
        higherYear: amt2 > amt1 ? 'previous' : amt1 > amt2 ? 'current' : 'tie',
        qty1Display: qty1.toFixed(2),
        amt1Display: formatIndianShort(amt1),
        qty2Display: qty2.toFixed(2),
        amt2Display: formatIndianShort(amt2)
      };
    });
  };

  const getTargetSale = async () => {
    try {
      const response = await fetchTargetSaleReport(user.userCode, partyCode);
      if (response.success) {
        const cleanNumber = (value) => Number(String(value ?? '0').replace(/,/g, '')) || 0;
        const getBeatLabel = (row = {}) => row.beat || row.beat_name || row.beat_code || row.route || '-';
        const getTargetAmount = (row = {}) => cleanNumber(row.target_amt ?? row.target);

        const ctdArray = response.data.ctd.map((row) => ({
          beat: getBeatLabel(row),
          partyName: row.party_name || 'Others',
          target1: getTargetAmount(row),
          sale1: cleanNumber(row.sale),
          target1Display: getTargetAmount(row).toFixed(2),
          sale1Display: formatIndianShort(row.sale)
        }));

        const ytdArray =
          response.data.ytd?.map((row) => ({
            beat: getBeatLabel(row),
            partyName: row.party_name || 'Others',
            target2: getTargetAmount(row),
            sale2: cleanNumber(row.sale),
            target2Display: getTargetAmount(row).toFixed(2),
            sale2Display: formatIndianShort(row.sale)
          })) || [];

        // Merge ctd and ytd by index (or category)
        const merged = ctdArray.map((row, index) => ({
          beat: row.beat || ytdArray[index]?.beat || '-',
          partyName: row.partyName,
          target1: row.target1,
          sale1: row.sale1,
          target2: ytdArray[index]?.target2 || 0,
          sale2: ytdArray[index]?.sale2 || 0,
          higherYear: (ytdArray[index]?.sale2 || 0) > row.sale1 ? 'previous' : row.sale1 > (ytdArray[index]?.sale2 || 0) ? 'current' : 'tie',
          target1Display: row.target1Display,
          sale1Display: row.sale1Display,
          target2Display: ytdArray[index]?.target2Display || formatIndianShort(0),
          sale2Display: ytdArray[index]?.sale2Display || formatIndianShort(0)
        }));
        setTargetSales(merged);
      }
    } catch (error) {
      console.error('Error fetching Target Sales Report:', error);
    }
  };

  return {
    cloading,
    customer,
    currentFY,
    previousFY,
    outstanding,
    sales,
    categoryComp,
    targetsales
  };
};

export default BeatsCustomerReportController;
